import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDrag, useDrop } from 'react-dnd';
import type { Feature, FeatureDepartment } from '../../../types/feature';
import {
  BU,
  FeatureRoleMatrix,
  getFeatureTargetBUs,
} from '../utils/featureRoleMatrix';
import { getDeptMeta } from './DeptBadge';
import SharedWithDots from './SharedWithDots';
import { getFeatureIcon } from '../utils/featureIcons';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';

export const SUB_MENU_DRAG_TYPE = 'feature-sub-menu';

export interface SubMenuDragItem {
  key: string;
  name: string;
  currentParentKey: string;
}

interface FeatureSectionRowProps {
  parent: Feature;
  childrenList: Feature[];
  matrix: FeatureRoleMatrix;
  editable: boolean;
  activeBU: BU;
  onEdit: (f: Feature) => void;
  onDelete: (key: string) => void;
  onToggleStatus: (key: string) => void;
  onMakeCommon: (key: string, currentDept: FeatureDepartment) => void;
  onAddSubMenu: (parentKey: string, dept: FeatureDepartment) => void;
  /** Reassign a sub-menu to a different parent. */
  onMoveSubMenu: (featureKey: string, newParentKey: string) => void;
}

const COL_COUNT = 5;

function totalRoleAccess(matrix: FeatureRoleMatrix, featureKey: string): number {
  const entry = matrix[featureKey];
  if (!entry) return 0;
  let n = 0;
  for (const bu of entry.targetBUs) {
    n += entry.byBU[bu]?.rolesWithAccess.length ?? 0;
  }
  return n;
}

function getAccentDept(feature: Feature): FeatureDepartment {
  const targetBUs = getFeatureTargetBUs(feature);
  if (targetBUs.length === 3) return 'COMMON';
  return targetBUs[0] as FeatureDepartment;
}

// Sovereign Emerald per-BU left-rail accent colours (inline to avoid JIT purge).
const ACCENT_BORDER_COLOR: Record<FeatureDepartment, string> = {
  COMMON: '#3F6B82',
  OMS: SOVEREIGN.brand,
  AMS: '#6B5A8A',
  EPC: SOVEREIGN.champagne,   // champagne gold — NOT orange
};

const FeatureSectionRow: React.FC<FeatureSectionRowProps> = ({
  parent,
  childrenList,
  matrix,
  editable,
  activeBU,
  onEdit,
  onDelete,
  onToggleStatus,
  onMakeCommon,
  onAddSubMenu,
  onMoveSubMenu,
}) => {
  const accentDept = getAccentDept(parent);
  const accentMeta = getDeptMeta(accentDept);
  const accentBorderColor = ACCENT_BORDER_COLOR[accentDept];
  const parentInactive = parent.isActive === false;
  const parentSelfRoleCount = totalRoleAccess(matrix, parent.key);
  const showParentAsFeatureRow = parentSelfRoleCount > 0;
  const parentTargetBUs = getFeatureTargetBUs(parent);

  return (
    <>
      <SectionHeader
        parent={parent}
        accentMeta={accentMeta}
        accentBorderColor={accentBorderColor}
        inactive={parentInactive}
        childrenCount={childrenList.length}
        editable={editable}
        activeBU={activeBU}
        accentDept={accentDept}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggleStatus={onToggleStatus}
        onMakeCommon={onMakeCommon}
        onAddSubMenu={onAddSubMenu}
        onMoveSubMenu={onMoveSubMenu}
      />

      {showParentAsFeatureRow && (
        <FeatureRow
          feature={parent}
          targetBUs={parentTargetBUs}
          roleCount={parentSelfRoleCount}
          editable={editable}
          isParentSelf
          onEdit={onEdit}
          onDelete={onDelete}
          onToggleStatus={onToggleStatus}
          onMakeCommon={onMakeCommon}
        />
      )}

      {childrenList.length === 0 ? (
        <tr>
          <td
            colSpan={COL_COUNT}
            className="px-6 py-3 text-xs italic"
            style={{ background: SOVEREIGN.cardBg, color: SOVEREIGN.kpiCaption }}
          >
            No sub-menus yet
            {editable && (
              <>
                {' — '}
                <button
                  className="font-semibold"
                  style={{ color: SOVEREIGN.bottle }}
                  onClick={() => onAddSubMenu(parent.key, activeBU as FeatureDepartment)}
                >
                  + Add sub-menu
                </button>
              </>
            )}
          </td>
        </tr>
      ) : (
        childrenList.map(child => (
          <FeatureRow
            key={child._id}
            feature={child}
            parentKey={parent.key}
            targetBUs={getFeatureTargetBUs(child)}
            roleCount={totalRoleAccess(matrix, child.key)}
            editable={editable}
            draggable
            onEdit={onEdit}
            onDelete={onDelete}
            onToggleStatus={onToggleStatus}
            onMakeCommon={onMakeCommon}
          />
        ))
      )}
    </>
  );
};

// ─── Section header ─────────────────────────────────────────────────────────

function SectionHeader({
  parent,
  accentMeta,
  accentBorderColor,
  inactive,
  childrenCount,
  editable,
  activeBU,
  accentDept,
  onEdit,
  onDelete,
  onToggleStatus,
  onMakeCommon,
  onAddSubMenu,
  onMoveSubMenu,
}: {
  parent: Feature;
  accentMeta: ReturnType<typeof getDeptMeta>;
  accentBorderColor: string;
  inactive: boolean;
  childrenCount: number;
  editable: boolean;
  activeBU: BU;
  accentDept: FeatureDepartment;
  onEdit: (f: Feature) => void;
  onDelete: (key: string) => void;
  onToggleStatus: (key: string) => void;
  onMakeCommon: (key: string, currentDept: FeatureDepartment) => void;
  onAddSubMenu: (parentKey: string, dept: FeatureDepartment) => void;
  onMoveSubMenu: (featureKey: string, newParentKey: string) => void;
}) {
  const [{ isOver, canDrop, draggedFromSameParent }, connectDrop] = useDrop<
    SubMenuDragItem,
    void,
    { isOver: boolean; canDrop: boolean; draggedFromSameParent: boolean }
  >({
    accept: SUB_MENU_DRAG_TYPE,
    canDrop: item => editable && item.currentParentKey !== parent.key,
    drop: item => {
      if (item.currentParentKey === parent.key) return;
      onMoveSubMenu(item.key, parent.key);
    },
    collect: monitor => {
      const item = monitor.getItem();
      return {
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
        draggedFromSameParent: !!item && item.currentParentKey === parent.key,
      };
    },
  });

  const activeDrop = isOver && canDrop;
  const sameParentHover = isOver && draggedFromSameParent;

  // Section header row — cream paper, left accent rail, no background gradient.
  const headerBg = activeDrop
    ? 'rgba(31,90,71,0.06)'
    : sameParentHover
    ? 'rgba(201,169,97,0.07)'
    : 'rgba(11,59,46,0.025)';

  return (
    <tr>
      <td colSpan={COL_COUNT} className="p-0" ref={el => { connectDrop(el); }}>
        <div
          className="flex items-center gap-3 pl-4 pr-4 py-2.5 transition-colors"
          style={{
            background: headerBg,
            borderLeft: `3px solid ${accentBorderColor}`,
            outline: activeDrop ? `2px solid ${SOVEREIGN.brand}` : undefined,
            outlineOffset: activeDrop ? '-2px' : undefined,
          }}
        >
          {/* Icon tile */}
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            aria-hidden
            style={{
              background: accentMeta.style.background,
              border: `1px solid ${accentMeta.style.borderColor}`,
              color: accentMeta.style.color,
            }}
          >
            {(() => {
              const Icon = getFeatureIcon(parent);
              return <Icon className="w-5 h-5" />;
            })()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-bold truncate" style={{ color: SOVEREIGN.title }}>
                {parent.name}
              </span>
              {inactive && (
                <span
                  className="inline-flex items-center px-1.5 py-0 rounded text-[10px] font-bold uppercase tracking-wider"
                  style={{ background: 'rgba(159,18,57,0.08)', color: '#9F1239', border: '1px solid rgba(159,18,57,0.20)' }}
                >
                  Inactive
                </span>
              )}
              <span className="text-[11px] font-mono truncate" style={{ color: SOVEREIGN.kpiCaption }}>
                {parent.route}
              </span>
              {activeDrop && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider animate-pulse text-white"
                  style={{ background: SOVEREIGN.btn }}
                >
                  Drop to move here
                </span>
              )}
              {sameParentHover && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                  style={{ background: 'rgba(201,169,97,0.18)', color: '#8A5A08' }}
                >
                  Already here
                </span>
              )}
            </div>
          </div>
          {/* Sub-menu count badge */}
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0"
            style={{
              background: 'rgba(11,59,46,0.06)',
              color: SOVEREIGN.ink,
              border: `1px solid ${SOVEREIGN.cardBorder}`,
            }}
          >
            {childrenCount} sub-menu{childrenCount === 1 ? '' : 's'}
          </span>
          {editable && (
            <button
              onClick={() => onAddSubMenu(parent.key, activeBU as FeatureDepartment)}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-colors flex-shrink-0"
              style={{ color: SOVEREIGN.bottle, border: '1px solid transparent' }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(31,90,71,0.07)';
                (e.currentTarget as HTMLButtonElement).style.borderColor = SOVEREIGN.cardBorder;
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'transparent';
              }}
            >
              + Add sub-menu
            </button>
          )}
          {editable && (
            <KebabMenu
              feature={parent}
              accentDept={accentDept}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleStatus={onToggleStatus}
              onMakeCommon={onMakeCommon}
            />
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Feature row (sub-menu, or parent-as-feature) ───────────────────────────

function FeatureRow({
  feature,
  parentKey,
  targetBUs,
  roleCount,
  editable,
  isParentSelf,
  draggable = false,
  onEdit,
  onDelete,
  onToggleStatus,
  onMakeCommon,
}: {
  feature: Feature;
  parentKey?: string;
  targetBUs: BU[];
  roleCount: number;
  editable: boolean;
  isParentSelf?: boolean;
  draggable?: boolean;
  onEdit: (f: Feature) => void;
  onDelete: (key: string) => void;
  onToggleStatus: (key: string) => void;
  onMakeCommon: (key: string, currentDept: FeatureDepartment) => void;
}) {
  const navigate = useNavigate();
  const accentDept = getAccentDept(feature);
  const accentMeta = getDeptMeta(accentDept);
  const inactive = feature.isActive === false;

  const canDrag = draggable && editable && !isParentSelf && !!parentKey;
  const [{ isDragging }, connectDrag] = useDrag<
    SubMenuDragItem,
    void,
    { isDragging: boolean }
  >(
    () => ({
      type: SUB_MENU_DRAG_TYPE,
      item: {
        key: feature.key,
        name: feature.name,
        currentParentKey: parentKey ?? '',
      },
      canDrag: () => canDrag,
      collect: monitor => ({ isDragging: monitor.isDragging() }),
    }),
    [feature.key, feature.name, parentKey, canDrag],
  );

  const openPermissions = () => {
    navigate(`/admin/features/${encodeURIComponent(feature.key)}/permissions`);
  };

  return (
    <tr
      className={`group/row transition-colors ${inactive ? 'opacity-60' : ''} ${isDragging ? 'opacity-40' : ''}`}
      style={{
        borderTop: `1px solid ${SOVEREIGN.innerRule}`,
        background: isDragging ? 'rgba(31,90,71,0.04)' : SOVEREIGN.cardBg,
      }}
      onMouseEnter={e => {
        if (!isDragging) (e.currentTarget as HTMLTableRowElement).style.background = 'rgba(11,59,46,0.025)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLTableRowElement).style.background = SOVEREIGN.cardBg;
      }}
    >
      {/* Col 1 — Feature */}
      <td className="px-4 py-2.5 align-middle">
        <div className="flex items-center gap-2 pl-6">
          {canDrag ? (
            <span
              ref={el => { connectDrag(el); }}
              role="button"
              tabIndex={-1}
              title="Drag to a different parent menu"
              aria-label="Drag handle — move to another parent"
              className="flex-shrink-0 -ml-4 mr-1 px-0.5 py-0.5 rounded cursor-grab active:cursor-grabbing opacity-40 group-hover/row:opacity-100 transition-opacity"
              style={{ color: SOVEREIGN.kpiCaption }}
              onMouseEnter={e => { (e.currentTarget as HTMLSpanElement).style.color = SOVEREIGN.bottle; }}
              onMouseLeave={e => { (e.currentTarget as HTMLSpanElement).style.color = SOVEREIGN.kpiCaption; }}
            >
              <DragHandleIcon />
            </span>
          ) : (
            <span className="flex-shrink-0 -ml-4 mr-1 w-3" aria-hidden />
          )}
          <span
            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ background: accentMeta.style.dotColor }}
            aria-hidden
          />
          {/* Icon tile */}
          <div
            className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
            aria-hidden
            style={{
              background: accentMeta.style.background,
              color: accentMeta.style.color,
              border: `1px solid ${accentMeta.style.borderColor}`,
            }}
          >
            {(() => {
              const Icon = getFeatureIcon(feature);
              return <Icon className="w-4 h-4" />;
            })()}
          </div>
          <span className="text-sm font-semibold truncate" style={{ color: SOVEREIGN.ink }}>
            {feature.name}
          </span>
          {isParentSelf && (
            <span
              className="text-[10px] uppercase tracking-wider font-semibold"
              style={{ color: SOVEREIGN.kpiCaption }}
            >
              (parent)
            </span>
          )}
          <SharedWithDots targetBUs={targetBUs} />
          {inactive && (
            <span
              className="inline-flex items-center px-1.5 py-0 rounded text-[10px] font-bold uppercase tracking-wider"
              style={{ background: 'rgba(159,18,57,0.08)', color: '#9F1239', border: '1px solid rgba(159,18,57,0.20)' }}
            >
              Inactive
            </span>
          )}
        </div>
      </td>

      {/* Col 2 — Route */}
      <td className="px-4 py-2.5 align-middle">
        <span
          className="text-xs font-mono truncate inline-block max-w-[280px] align-middle"
          style={{ color: SOVEREIGN.kpiCaption }}
          title={feature.route}
        >
          {feature.route}
        </span>
      </td>

      {/* Col 3 — Permission key */}
      <td className="px-4 py-2.5 align-middle">
        <span
          className="text-xs font-mono truncate inline-block max-w-[200px] align-middle"
          style={{ color: SOVEREIGN.kpiCaption }}
          title={feature.permissionKey}
        >
          {feature.permissionKey}
        </span>
      </td>

      {/* Col 4 — Roles count */}
      <td className="px-4 py-2.5 align-middle text-right">
        <button
          type="button"
          onClick={openPermissions}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold transition-colors"
          style={{
            background: 'rgba(11,59,46,0.05)',
            color: SOVEREIGN.bottle,
            border: `1px solid ${SOVEREIGN.cardBorder}`,
          }}
          title={`${roleCount} role grant${roleCount === 1 ? '' : 's'} — open feature permissions`}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(31,90,71,0.10)';
            (e.currentTarget as HTMLButtonElement).style.color = SOVEREIGN.title;
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(11,59,46,0.05)';
            (e.currentTarget as HTMLButtonElement).style.color = SOVEREIGN.bottle;
          }}
        >
          <span aria-hidden>👥</span>
          {roleCount}
        </button>
      </td>

      {/* Col 5 — Actions */}
      <td className="px-2 py-2.5 align-middle text-right">
        {editable && (
          <KebabMenu
            feature={feature}
            accentDept={accentDept}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggleStatus={onToggleStatus}
            onMakeCommon={onMakeCommon}
          />
        )}
      </td>
    </tr>
  );
}

// ─── Kebab menu ─────────────────────────────────────────────────────────────

function KebabMenu({
  feature,
  accentDept,
  onEdit,
  onDelete,
  onToggleStatus,
  onMakeCommon,
}: {
  feature: Feature;
  accentDept: FeatureDepartment;
  onEdit: (f: Feature) => void;
  onDelete: (key: string) => void;
  onToggleStatus: (key: string) => void;
  onMakeCommon: (key: string, currentDept: FeatureDepartment) => void;
}) {
  const [open, setOpen] = useState(false);
  const isCommon = useMemo(() => getFeatureTargetBUs(feature).length === 3, [feature]);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={e => {
          e.stopPropagation();
          setOpen(v => !v);
        }}
        className="p-1.5 rounded-md transition-colors"
        style={{ color: SOVEREIGN.kpiCaption }}
        title="More actions"
        aria-label="More actions"
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(11,59,46,0.07)';
          (e.currentTarget as HTMLButtonElement).style.color = SOVEREIGN.ink;
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
          (e.currentTarget as HTMLButtonElement).style.color = SOVEREIGN.kpiCaption;
        }}
      >
        <span className="block w-4 text-base leading-none">⋮</span>
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            className="absolute right-0 top-full mt-1 rounded-lg z-20 min-w-[200px] overflow-hidden"
            style={{
              background: SOVEREIGN.cardBg,
              border: `1px solid ${SOVEREIGN.cardBorder}`,
              boxShadow: SOVEREIGN.cardShadow,
            }}
          >
            <MenuItem
              icon="✏️"
              label="Edit"
              onClick={() => {
                onEdit(feature);
                setOpen(false);
              }}
            />
            <MenuItem
              icon={isCommon ? '🔒' : '🌐'}
              label={isCommon ? 'Move to single BU' : 'Make Common'}
              onClick={() => {
                onMakeCommon(feature.key, accentDept);
                setOpen(false);
              }}
            />
            <MenuItem
              icon={feature.isActive ? '⛔' : '✅'}
              label={feature.isActive ? 'Deactivate' : 'Activate'}
              onClick={() => {
                onToggleStatus(feature.key);
                setOpen(false);
              }}
            />
            <div style={{ borderTop: `1px solid ${SOVEREIGN.innerRule}` }} />
            <MenuItem
              icon="🗑️"
              label="Delete"
              tone="danger"
              onClick={() => {
                onDelete(feature.key);
                setOpen(false);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  tone = 'default',
}: {
  icon: string;
  label: string;
  onClick: () => void;
  tone?: 'default' | 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors text-left"
      style={{ color: tone === 'danger' ? '#9F1239' : SOVEREIGN.ink }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLButtonElement).style.background =
          tone === 'danger' ? 'rgba(159,18,57,0.06)' : 'rgba(11,59,46,0.04)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
      }}
    >
      <span className="text-sm" aria-hidden>
        {icon}
      </span>
      <span>{label}</span>
    </button>
  );
}

// ─── Drag handle icon ───────────────────────────────────────────────────────

function DragHandleIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="currentColor"
      aria-hidden
    >
      <circle cx="4" cy="2" r="1" />
      <circle cx="8" cy="2" r="1" />
      <circle cx="4" cy="6" r="1" />
      <circle cx="8" cy="6" r="1" />
      <circle cx="4" cy="10" r="1" />
      <circle cx="8" cy="10" r="1" />
    </svg>
  );
}

export default FeatureSectionRow;
